/** Default start hour (24h) for new Add to Calendar events */
export const DEFAULT_START_HOUR = 9;

/** Default event duration in minutes */
export const DEFAULT_EVENT_DURATION_MINUTES = 30;

/** Default start time string for time inputs (HH:mm) */
export const DEFAULT_START_TIME = `${String(DEFAULT_START_HOUR).padStart(2, "0")}:00`;

/** Default end time string for time inputs (HH:mm), start + duration */
const startTotalMinutes = DEFAULT_START_HOUR * 60;
const endTotalMinutes = startTotalMinutes + DEFAULT_EVENT_DURATION_MINUTES;
const defaultEndHour = Math.floor(endTotalMinutes / 60);
const defaultEndMinute = endTotalMinutes % 60;
export const DEFAULT_END_TIME = `${String(defaultEndHour).padStart(2, "0")}:${String(defaultEndMinute).padStart(2, "0")}`;

/**
 * Parse "HH:mm" to hours and minutes. Returns null if invalid.
 */
function parseTimeString(
  time: string,
): { hours: number; minutes: number } | null {
  if (!time?.trim()) return null;
  const parts = time.trim().split(":");
  const hours = parseInt(parts[0], 10);
  const minutes = parts[1] != null ? parseInt(parts[1], 10) : 0;
  if (Number.isNaN(hours) || hours < 0 || hours > 23) return null;
  if (Number.isNaN(minutes) || minutes < 0 || minutes > 59) return null;
  return { hours, minutes };
}

/**
 * Build start and end Date for "today" at the given HH:mm times (local time).
 * Uses DEFAULT_START_TIME / DEFAULT_END_TIME when strings are empty or invalid.
 */
export function getEventStartEndDates(
  startTime: string,
  endTime: string,
): { startDate: Date; endDate: Date } {
  const now = new Date();
  const startDate = new Date(now);
  const startParsed = parseTimeString(startTime);
  if (startParsed) {
    startDate.setHours(startParsed.hours, startParsed.minutes, 0, 0);
  } else {
    startDate.setHours(DEFAULT_START_HOUR, 0, 0, 0);
  }
  const endDate = new Date(startDate);
  const endParsed = parseTimeString(endTime);
  if (endParsed) {
    endDate.setHours(endParsed.hours, endParsed.minutes, 0, 0);
  } else {
    endDate.setMinutes(startDate.getMinutes() + DEFAULT_EVENT_DURATION_MINUTES);
  }
  return { startDate, endDate };
}

/** Format Date for calendar URLs/ICS (UTC): YYYYMMDDTHHMMSSZ */
export function formatDateForCalendar(date: Date): string {
  return date.toISOString().replace(/[-:]/g, "").split(".")[0].concat("Z");
}
